import { PlaylistEntry, usePlaylistCurrentlySelected } from '@statsbomb/video-client-js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EventDescription } from '@/types/event';
import { Nullable } from '@/types/generic';
import { SelectedVideoIds } from '@/types/video';
import { formatGameTimestamp } from '@/utils/games';
import { isNullish } from '@/utils/predicates';
import { snakeToCamel } from '@/utils/queries';

export const useFormatVideoEvents = (
  selectedVideoIds: Nullable<SelectedVideoIds[]>,
  descriptionsData: EventDescription[],
): PlaylistEntry[] => {
  const { t } = useTranslation('events');
  const [currentlySelectedVideo] = usePlaylistCurrentlySelected();

  // build once so sort + map both get O(1) lookups instead of O(n) each
  const descriptionMap = useMemo(
    () =>
      descriptionsData.reduce((map, description) => {
        map.set(description.eventId, description);
        return map;
      }, new Map<string, EventDescription>()),
    [descriptionsData],
  );

  return useMemo(() => {
    if (!selectedVideoIds) return [];

    // precompute timestamps once — avoids new Date() construction inside the O(n log n) sort comparator
    const gameDateTimestamps = new Map<string, number>(
      descriptionsData
        .filter(
          (description): description is EventDescription & { gameDate: string } => !isNullish(description.gameDate),
        )
        .map(({ eventId, gameDate }) => [eventId, new Date(gameDate).getTime()]),
    );

    return [...selectedVideoIds]
      .sort((firstVideo, secondVideo) => {
        const firstEvent = descriptionMap.get(firstVideo.eventId);
        const secondEvent = descriptionMap.get(secondVideo.eventId);

        // Sort missing data to the end of the list
        if (!firstEvent && !secondEvent) return 0;
        if (!firstEvent) return 1;
        if (!secondEvent) return -1;

        // 1. Primary sort: game date descending — newer games appear first in the playlist
        const firstDateTimestamp = gameDateTimestamps.get(firstEvent.eventId) ?? 0;
        const secondDateTimestamp = gameDateTimestamps.get(secondEvent.eventId) ?? 0;
        const dateComparisonResult = secondDateTimestamp - firstDateTimestamp;
        if (dateComparisonResult !== 0) return dateComparisonResult;

        // 2. Secondary sort: keep events from different games on the same date grouped together
        if (firstEvent.gameId !== secondEvent.gameId) return firstEvent.gameId - secondEvent.gameId;

        // 3. Tertiary sort: chronological timeline within a match ascending
        if (firstEvent.period !== secondEvent.period) return firstEvent.period - secondEvent.period;
        if (firstEvent.minute !== secondEvent.minute) return firstEvent.minute - secondEvent.minute;
        return firstEvent.second - secondEvent.second;
      })
      .filter(entry => !!entry.videoEventId)
      .map(({ eventId, videoEventId }) => {
        const coreVideoData = {
          id: videoEventId,
          selectedEntry: videoEventId === currentlySelectedVideo?.id,
        };

        const eventDescription = descriptionMap.get(eventId);

        if (!eventDescription) return coreVideoData;

        const eventType = t(`fields.event.type.${snakeToCamel(eventDescription.type)}`);
        const timestamp = formatGameTimestamp(eventDescription.minute, eventDescription.second);
        const { gameName, competitionName, gameDate } = eventDescription;

        return {
          ...coreVideoData,
          description: t('playlist.description', {
            player: eventDescription.playerDisplayName,
            eventType,
            interpolation: { escapeValue: false },
          }),
          descriptionLineTwo: gameName
            ? t('playlist.descriptionLineTwo', { timestamp, gameName, interpolation: { escapeValue: false } })
            : t('playlist.descriptionLineTwoNoGame', { timestamp }),
          descriptionLineThree: gameDate
            ? t('playlist.descriptionLineThree', {
                competition: competitionName,
                date: gameDate,
                interpolation: { escapeValue: false },
              })
            : t('playlist.descriptionLineThreeNoDate', {
                competition: competitionName,
                interpolation: { escapeValue: false },
              }),
        };
      });
  }, [selectedVideoIds, descriptionMap, currentlySelectedVideo?.id, t]);
};
